'use client'

import { useRef, useEffect, useCallback } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { useIsMobile } from '@/hooks/use-mobile'
import { elementProps } from '@/lib/elementHelpers'
import GrandTitle from '@/components/GrandTitle'
import type { SectionConfig } from '@/types/site'

gsap.registerPlugin(ScrollTrigger, SplitText)

interface ProcessItem {
  id: string
  title: string
  description: string
  badge?: string
  details?: string
  image?: string
}

export function JlstudioProcessTimeline({
  config,
  isEditing,
}: {
  config: SectionConfig
  isEditing?: boolean
}) {
  const content = (config.content ?? {}) as Record<string, unknown>
  const items = (content.items as ProcessItem[]) ?? []
  const eyebrow = content.eyebrow as string | undefined
  const title = content.title as string | undefined
  const subtitle = content.subtitle as string | undefined
  const dressCode = content.dressCode as string | undefined
  const accent = config.style?.accentColor ?? '#638BFF'
  // Fond bleu nuit premium quand la section est déclarée 'dark' (programme du Gala).
  const isDark = config.style?.background === 'dark'
  const isPreview = !isEditing

  const sectionRef = useRef<HTMLDivElement>(null)
  const lineRef = useRef<HTMLDivElement>(null)
  const lineProgressRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()

  // Set line height to match timeline content
  useEffect(() => {
    if (!sectionRef.current || !lineRef.current) return
    const stepEls = sectionRef.current.querySelectorAll<HTMLElement>('[data-step]')
    if (stepEls.length === 0) return
    const lastStep = stepEls[stepEls.length - 1]
    // Mobile: node aligned with the hour at ~32px from step top.
    // Desktop: node vertically centered.
    const nodeOffsetInStep = isMobile ? 32 : lastStep.offsetHeight / 2
    const lineHeight = lastStep.offsetTop + nodeOffsetInStep
    lineRef.current.style.height = `${lineHeight}px`
  }, [isMobile, items.length])

  // Scroll-driven line progress — works everywhere (no ScrollTrigger needed)
  // Uses getBoundingClientRect to calculate progress manually
  const updateLineProgress = useCallback(() => {
    if (!lineRef.current || !lineProgressRef.current) return
    const rect = lineRef.current.getBoundingClientRect()
    const vh = window.innerHeight
    // Progress: 0 when line top enters viewport at 60%, 1 when line bottom reaches 40%
    const start = vh * 0.6
    const end = vh * 0.4
    const totalTravel = rect.height + (start - end)
    const traveled = start - rect.top
    const progress = Math.max(0, Math.min(1, traveled / totalTravel))
    lineProgressRef.current.style.transform = `scaleY(${progress})`
    lineProgressRef.current.style.transformOrigin = 'top'
  }, [])

  useEffect(() => {
    if (!isPreview) {
      // In edit mode, show full line
      if (lineProgressRef.current) {
        lineProgressRef.current.style.transform = 'scaleY(1)'
      }
      return
    }

    // Find the scroll container (could be window or a parent div in builder)
    let scrollContainer: HTMLElement | Window = window
    if (sectionRef.current) {
      let parent = sectionRef.current.parentElement
      while (parent) {
        const style = window.getComputedStyle(parent)
        if ((style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll') && parent !== document.documentElement) {
          scrollContainer = parent
          break
        }
        parent = parent.parentElement
      }
    }

    // Start with line hidden
    if (lineProgressRef.current) {
      lineProgressRef.current.style.transform = 'scaleY(0)'
      lineProgressRef.current.style.transformOrigin = 'top'
    }

    const onScroll = () => requestAnimationFrame(updateLineProgress)
    scrollContainer.addEventListener('scroll', onScroll, { passive: true })
    onScroll() // initial calc

    return () => scrollContainer.removeEventListener('scroll', onScroll)
  }, [isPreview, updateLineProgress])

  // GSAP animations for final deployed site only
  useEffect(() => {
    if (!sectionRef.current || !isPreview) return

    // Detect builder environment: skip GSAP animations
    const el = sectionRef.current
    let parent = el.parentElement
    let isInBuilder = false
    while (parent) {
      const style = window.getComputedStyle(parent)
      if ((style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll') && parent !== document.documentElement) {
        isInBuilder = true
        break
      }
      parent = parent.parentElement
    }
    if (isInBuilder) return

    const section = sectionRef.current
    const stepEls = section.querySelectorAll<HTMLElement>('[data-step]')
    const numberEls = section.querySelectorAll<HTMLElement>('[data-number]')
    const titleEls = section.querySelectorAll<HTMLElement>('[data-title]')
    const descEls = section.querySelectorAll<HTMLElement>('[data-desc]')
    const detailEls = section.querySelectorAll<HTMLElement>('[data-detail]')
    const nodeEls = section.querySelectorAll<HTMLElement>('[data-node]')
    const nodeInners = section.querySelectorAll<HTMLElement>('[data-node-inner]')

    if (stepEls.length === 0) return

    // Skip animations if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      titleEls.forEach((t) => {
        const s = SplitText.create(t, { type: 'words,chars' })
        gsap.set(s.chars, { opacity: 1, y: 0, rotateX: 0 })
      })
      numberEls.forEach((n) => gsap.set(n, { opacity: 1, y: 0 }))
      descEls.forEach((d) => gsap.set(d, { opacity: 1, y: 0 }))
      detailEls.forEach((d) => gsap.set(d, { opacity: 1, y: 0 }))
      nodeEls.forEach((n) => gsap.set(n, { scale: 1, opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }))
      return
    }

    const ctx = gsap.context(() => {
      const splitTitles = Array.from(titleEls).map((el) =>
        SplitText.create(el, { type: 'words,chars' })
      )

      // Per-step animations
      stepEls.forEach((stepEl, i) => {
        const number = numberEls[i]
        const desc = descEls[i]
        const detail = detailEls[i]
        const node = nodeEls[i]
        const nodeInner = nodeInners[i]
        const split = splitTitles[i]
        const isLeft = i % 2 === 0

        // Initial states
        gsap.set(split.chars, { opacity: 0, y: isMobile ? 30 : 50, rotateX: isMobile ? -30 : -60 })
        gsap.set(desc, { opacity: 0, y: 30 })
        if (detail) gsap.set(detail, { opacity: 0, y: 20 })
        gsap.set(number, { opacity: 0, y: 20 })
        gsap.set(node, { scale: 0, opacity: 0 })

        // Parallax: number moves slower
        gsap.to(number, {
          yPercent: isMobile ? -10 : -25, ease: 'none',
          scrollTrigger: { trigger: stepEl, start: 'top bottom', end: 'bottom top', scrub: true },
        })

        // Parallax: title at medium speed
        gsap.to(titleEls[i], {
          yPercent: isMobile ? -8 : -15, ease: 'none',
          scrollTrigger: { trigger: stepEl, start: 'top bottom', end: 'bottom top', scrub: true },
        })

        // Parallax: description slight
        gsap.to(desc, {
          yPercent: isMobile ? -3 : -5, ease: 'none',
          scrollTrigger: { trigger: stepEl, start: 'top bottom', end: 'bottom top', scrub: true },
        })

        // Node scale up + glow
        gsap.to(node, {
          scale: 1, opacity: 1, duration: 0.6, ease: 'back.out(2)',
          scrollTrigger: { trigger: stepEl, start: 'top 70%', toggleActions: 'play none none reverse' },
        })

        if (nodeInner) {
          gsap.to(nodeInner, {
            boxShadow: `0 0 20px ${accent}99, 0 0 40px ${accent}33`,
            duration: 0.8, ease: 'power2.out',
            scrollTrigger: { trigger: stepEl, start: 'top 60%', toggleActions: 'play none none reverse' },
          })
        }

        // Number fade in
        gsap.to(number, {
          opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: stepEl, start: 'top 75%', toggleActions: 'play none none reverse' },
        })

        // Title chars cascade
        gsap.to(split.chars, {
          opacity: 1, y: 0, rotateX: 0,
          stagger: isMobile ? 0.03 : (isLeft ? 0.04 : -0.04),
          duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: stepEl, start: 'top 68%', toggleActions: 'play none none reverse' },
        })

        // Description slides in
        gsap.to(desc, {
          opacity: 1, y: 0, duration: 0.8, ease: 'power2.out',
          scrollTrigger: { trigger: stepEl, start: 'top 62%', toggleActions: 'play none none reverse' },
        })

        if (detail) {
          gsap.to(detail, {
            opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
            scrollTrigger: { trigger: stepEl, start: 'top 58%', toggleActions: 'play none none reverse' },
          })
        }
      })
    }, sectionRef)

    return () => { ctx.revert() }
  }, [isMobile, isPreview, accent])


  return (
    <section
      ref={sectionRef}
      className={isDark ? "relative overflow-hidden" : "relative bg-brand-cream overflow-hidden"}
      style={{
        fontFamily: 'var(--font-body, inherit)',
        clipPath: 'inset(0)',
        background: isDark ? 'radial-gradient(ellipse 66% 62% at 50% 44%, #0a2247, #04173a 62%, #021734 100%)' : undefined,
      }}
    >
      {/* Fond d'ambiance (lueurs + dégradé noir haut/bas) retiré : le fond de la
          timeline doit être exactement le même bg-brand-cream que les autres
          sections, sans teinte ni assombrissement des bords. */}

      {/* Section header — aligné sur la variant obscura-centered (Le Lieu / Manifeste) pour cohérence du langage typographique */}
      <div
        className="relative z-10 text-center mx-auto"
        style={{
          maxWidth: '1100px',
          padding: 'clamp(48px, 9vw, 120px) clamp(20px, 5vw, 60px) clamp(40px, 6vw, 80px)',
        }}
      >
        {eyebrow && (
          <>
            <p
              style={{
                fontFamily: 'var(--font-body, inherit)',
                fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: accent,
                fontWeight: 600,
                marginBottom: '22px',
              }}
            >
              {eyebrow}
            </p>
            <div style={{ width: 46, height: 1, backgroundColor: accent, margin: '0 auto 26px' }} aria-hidden />
          </>
        )}
        {title && (
          <GrandTitle
            dark={isDark}
            color={isDark ? '#F7EFE1' : '#021734'}
            editProps={elementProps(config.id, 'title', 'heading')}
            className="mx-auto"
            style={{ maxWidth: '1120px', margin: '0 auto 24px' }}
          >
            {title}
          </GrandTitle>
        )}
        {subtitle && (
          <p
            {...elementProps(config.id, 'subtitle', 'text')}
            style={{
              fontSize: 'clamp(15px, 0.875rem + 0.5vw, 18px)',
              fontWeight: 400,
              lineHeight: '160%',
              color: isDark ? 'rgba(255,255,255,0.74)' : 'rgba(22, 36, 58, 0.72)',
              maxWidth: '640px',
              margin: '0 auto',
            }}
          >
            {subtitle}
          </p>
        )}
        {dressCode && (
          <div className="mt-8 md:mt-10 px-6">
            <div
              className="inline-block px-5 py-3 md:px-6 md:py-3.5 border"
              style={{
                borderColor: `${accent}66`,
                backgroundColor: `${accent}0a`,
              }}
            >
              <p
                className="text-[11px] md:text-xs tracking-[0.3em] uppercase font-medium"
                style={{ color: accent }}
              >
                {dressCode}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Timeline container */}
      <div className="relative z-10 pb-20 md:pb-32">
        {/* Vertical line */}
        <div
          ref={lineRef}
          className="absolute top-0 w-[1px]"
          style={{
            left: isMobile ? '24px' : '50%',
            transform: isMobile ? 'none' : 'translateX(-50%)',
          }}
        >
          {/* Background line (faint) */}
          <div className="w-full h-full bg-white/[0.06]" />
          {/* Animated progress line */}
          <div
            ref={lineProgressRef}
            className="absolute top-0 left-0 w-full h-full"
            style={{
              background: `linear-gradient(to bottom, ${accent}99, ${accent}66, ${accent}99)`,
              transformOrigin: 'top',
              transform: 'scaleY(0)',
              transition: 'none',
            }}
          />
        </div>

        {/* Steps */}
        {items.map((item, i) => {
          const isLeft = i % 2 === 0
          const stepNumber = item.badge || String(i + 1).padStart(2, '0')

          return (
            <div key={item.id} data-step className={isMobile ? "relative py-5" : "relative py-6 md:py-8 lg:py-10"}>
              {/* Node on the line */}
              <div
                data-node
                className={isMobile ? "absolute z-20" : "absolute top-1/2 z-20"}
                style={{
                  left: isMobile ? '24px' : '50%',
                  top: isMobile ? '32px' : undefined,
                  transform: 'translate(-50%, -50%) scale(1)',
                }}
              >
                <div
                  data-node-inner
                  className={isMobile ? "w-2.5 h-2.5 rounded-full" : "w-4 h-4 rounded-full border-2"}
                  style={{
                    backgroundColor: accent,
                    borderColor: accent,
                    boxShadow: `0 0 12px ${accent}99`,
                  }}
                />
              </div>

              {/* Content */}
              {isMobile ? (
                <div className="relative pl-12 pr-4" style={{ perspective: 600 }}>
                  <span
                    data-number
                    className="block text-[11px] tracking-[0.3em] uppercase font-medium mb-1.5 will-change-transform"
                    style={{ color: accent, fontFamily: 'var(--font-body, inherit)' }}
                  >
                    {stepNumber}
                  </span>
                  <h3
                    data-title
                    {...elementProps(config.id, `items.${i}.title`, 'heading')}
                    className="text-lg font-semibold mb-2 leading-tight relative will-change-transform"
                    style={{ fontFamily: 'var(--font-heading, inherit)', color: isDark ? '#ffffff' : undefined }}
                  >
                    {item.title}
                  </h3>
                  <p
                    data-desc
                    {...elementProps(config.id, `items.${i}.description`, 'text')}
                    className="text-[13px] leading-[1.55] mb-1.5 relative will-change-transform"
                    style={{ color: isDark ? 'rgba(255,255,255,0.68)' : 'rgba(22,36,58,0.6)', whiteSpace: 'pre-line' }}
                  >
                    {item.description}
                  </p>
                  {item.details && (
                    <p
                      data-detail
                      className="text-[11px] tracking-wide relative"
                      style={{ color: `${accent}8c` }}
                    >
                      {item.details}
                    </p>
                  )}
                </div>
              ) : (
                <div className="relative max-w-7xl mx-auto px-8 grid grid-cols-2 gap-16 items-center">
                  <div
                    className={`relative ${isLeft ? 'text-right pr-16' : 'text-left pl-16 order-2'}`}
                    style={{ perspective: 600 }}
                  >
                    <span
                      data-number
                      className="text-5xl lg:text-6xl font-black block mb-3 leading-none will-change-transform"
                      style={{ color: isDark ? `${accent}cc` : accent, fontFamily: 'var(--font-heading, inherit)' }}
                    >
                      {stepNumber}
                    </span>
                    <h3
                      data-title
                      {...elementProps(config.id, `items.${i}.title`, 'heading')}
                      className="text-3xl md:text-4xl lg:text-4xl font-black mb-5 leading-[0.95] tracking-tight relative will-change-transform text-balance"
                      style={{ fontFamily: 'var(--font-heading, inherit)', color: isDark ? '#ffffff' : undefined }}
                    >
                      {item.title}
                    </h3>
                    <p
                      data-desc
                      {...elementProps(config.id, `items.${i}.description`, 'text')}
                      className="text-base lg:text-lg leading-relaxed mb-4 relative will-change-transform"
                      style={{ color: isDark ? 'rgba(255,255,255,0.68)' : 'rgba(22,36,58,0.6)', whiteSpace: 'pre-line' }}
                    >
                      {item.description}
                    </p>
                    {item.details && (
                      <p
                        data-detail
                        className="text-sm tracking-wide relative"
                        style={{ color: `${accent}8c` }}
                      >
                        {item.details}
                      </p>
                    )}
                  </div>
                  <div className={isLeft ? '' : 'order-1'} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
